"use client";

import { useState, type RefObject } from "react";
import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import {
  VIEWER_MODULE_COLUMN_MAX,
  VIEWER_MODULE_COLUMN_MIN,
  VIEWER_MODULE_COLUMN_RATIO,
  VIEWER_SPLIT_MIN_WIDTH,
  VIEWER_STACK_MAX_WIDTH,
} from "@/config/layout";

export type ViewerLayout = "stack" | "split";

export interface ViewerLayoutState {
  layout: ViewerLayout;
  width: number;
  height: number;
  moduleColumnWidth: number;
}

const UNMEASURED: ViewerLayoutState = {
  layout: "stack",
  width: 0,
  height: 0,
  moduleColumnWidth: 0,
};

export function resolveViewerLayout(
  width: number,
  height: number,
): ViewerLayoutState {
  const split = width >= VIEWER_SPLIT_MIN_WIDTH && width > height;
  const moduleColumnWidth = split
    ? Math.round(
        Math.min(
          VIEWER_MODULE_COLUMN_MAX,
          Math.max(VIEWER_MODULE_COLUMN_MIN, width * VIEWER_MODULE_COLUMN_RATIO),
        ),
      )
    : Math.min(width, VIEWER_STACK_MAX_WIDTH);
  return {
    layout: split ? "split" : "stack",
    width,
    height,
    moduleColumnWidth,
  };
}

export function useViewerLayout(
  containerRef: RefObject<HTMLElement | null>,
): ViewerLayoutState {
  const [state, setState] = useState<ViewerLayoutState>(UNMEASURED);

  useIsomorphicLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (width <= 0 || height <= 0) return;
      setState((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : resolveViewerLayout(width, height),
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [containerRef]);

  return state;
}
